/**
 * A RovoLogo is a subset API that works for both @atlaskit/logo and @atlaskit/icon
 * We use one or the other depending on 1P or 3P and need a consistent API internall
 */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ThirdPartyLogoProps } from '@atlassian/logo-third-party/types';

import { css, cssMap, jsx } from '@compiled/react';

import type { Size as IconSize } from '@atlaskit/icon/types';
import type { LogoProps } from '@atlaskit/logo/types';

import { SVGIcon } from '../../ui/icon';

type Size = 'small' | 'medium' | 'large';

type CorrectableRovoSize = 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large';

const isCorrectableRovoSize = (size: string): size is CorrectableRovoSize => {
	return ['xxsmall', 'xsmall', 'small', 'medium', 'large'].includes(size);
};

export type RovoLogoGlyphWrapper = (
	glyph: (props?: Pick<ThirdPartyLogoProps, 'size'>) => JSX.Element,
	defaultLabel?: string,
	defaultSize?: Size,
) => RovoLogo;
export type RovoLogoLogoWrapper = (LogoComponent: any, defaultLabel?: string) => RovoLogo;

export type RovoLogoProps = {
	size?: LogoProps['size'];
	label?: string;
	testId?: string;
};

export type RovoLogo = (props: RovoLogoProps) => JSX.Element;

/**
 * Due a bug in ADS custom icons are one size smaller (than atlaskit/logo)
 * To accommodate for this behavior you need to use one size bigger than what you need
 * See: https://atlassian.slack.com/archives/CFJ9DU39U/p1714096323217799
 */
const correctedSize: Record<CorrectableRovoSize, IconSize> = {
	xxsmall: 'small',
	xsmall: 'small',
	small: 'medium',
	medium: 'large',
	large: 'xlarge',
};

const sizeStyles = cssMap({
	small: {
		width: '16px',
		height: '16px',
	},
	medium: {
		width: '24px',
		height: '24px',
	},
	large: {
		width: '32px',
		height: '32px',
	},
	xlarge: {
		width: '48px',
		height: '48px',
	},
});

/**
 * A wrapper for providing an internal \@atlaskit/icon - alike experience for working with custom Rovo Icons
 *
 * Internally uses <Icon glyph={glyph} ... />
 *
 * @param {} glyph function returning an SVG as JSX
 * @param defaultLabel an accessible label for the logo
 */
export const RovoLogoFromGlyph: RovoLogoGlyphWrapper = (
	Glyph,
	defaultLabel = '',
	defaultSize: Size = 'medium',
) => {
	return ({ size = defaultSize, label = defaultLabel, testId }) => {
		const mappedSize = size && isCorrectableRovoSize(size) ? correctedSize[size] : defaultSize;
		return (
			<span
				aria-label={label || undefined}
				data-testid={testId}
				css={[sizeStyles[mappedSize]]}
				role={label ? 'img' : 'presentation'}
			>
				{fg('rovo-search-team-eu-26-brand-icon-library') ? (
					<Glyph
						size={
							mappedSize === 'small'
								? 'xxsmall'
								: mappedSize === 'medium'
									? 'small'
									: mappedSize === 'large'
										? 'medium'
										: 'xlarge'
						}
					/>
				) : (
					<Glyph />
				)}
			</span>
		);
	};
};

/**
 * A wrapper for rendering 3P svg illustrations in a similar way for atlaskit icon components
 *
 * @param {} glyph function returning an SVG as JSX
 * @param defaultLabel an accessible label for the logo
 */
export const RovoLogoFromSVG: RovoLogoGlyphWrapper = (
	glyph,
	defaultLabel = '',
	defaultSize?: Size,
) => {
	return ({ label = defaultLabel, size = defaultSize, testId }) => (
		<SVGIcon
			label={label}
			glyph={glyph}
			size={size && isCorrectableRovoSize(size) ? correctedSize[size] : 'medium'}
		/>
	);
};

/**
 * A wrapper for providing an internal \@atlaskit/icon - alike experience for working with \@atlaskit/logo
 *
 * Internally uses <LogoComponent ... />
 *
 * @param {} LogoComponent an import from \@atlaskit/logo
 * @param defaultLabel an accessible label for the logo
 */
export const RovoLogoFromLogo: RovoLogoLogoWrapper = (LogoComponent, defaultLabel = '') => {
	return ({ size, label, testId }) => {
		return (
			<LogoComponent
				label={label || defaultLabel}
				testId={testId}
				size={size || 'medium'}
				appearance="brand"
			/>
		);
	};
};

/**
 * Takes a logo component and returns a new logo component that uses the new logo design.
 */
export function RovoIconFromIcon(
	Icon: (props: LogoProps) => React.ReactNode,
): (props: LogoProps) => JSX.Element {
	return (props: LogoProps) => <Icon {...props} />;
}

export function get3PLogo(iconGlyph: () => JSX.Element, iconLabel: string, size?: Size): RovoLogo {
	return RovoLogoFromSVG(iconGlyph, iconLabel, size);
}

const imageStyles = css({
	width: '100%',
	height: '100%',
	objectFit: 'contain',
});

/**
 * Helper function to convert an image URL into a glyph function
 *
 * @param imageUrl URL string pointing to the image
 * @returns A glyph function that returns an img element
 */
export const createGlyphFromURL: (imageUrl: string) => () => JSX.Element = (imageUrl: string) => {
	return () => {
		return <img src={imageUrl} alt="" css={imageStyles} />;
	};
};

/**
 * Creates a RovoLogo from a URL. Useful for Forge connectors that provide icon URLs.
 * Uses the existing RovoLogoFromSVG infrastructure for consistent handling.
 */
export function get3PLogoFromURL(iconUrl: string, iconLabel: string, size?: Size): RovoLogo {
	return RovoLogoFromSVG(createGlyphFromURL(iconUrl), iconLabel, size);
}
